import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, ClipboardCheck } from 'lucide-react';
import { Badge } from '@/components/common/Badge';
import { Card } from '@/components/common/Card';
import { EmptyState, ErrorState, LoadingState } from '@/components/common/States';
import { ROUTES } from '@/app/router/routes';
import { portalApi } from '@/services/api/endpoints/portal';
import { queryKeys } from '@/services/api/queryKeys';
import { formatDate } from '@/utils/format';
import { PortalShell, type PortalContext } from './PortalShell';
import { percentText } from './shared';

/** Published results only, newest first — the server's filter and order. */
export function PortalResultsPage() {
  return (
    <PortalShell title="Results" icon={<ClipboardCheck className="h-5 w-5" aria-hidden="true" />}>
      {(context) => <Results context={context} />}
    </PortalShell>
  );
}

function Results({ context }: { context: PortalContext }) {
  const { subject, subjectKey, withChild } = context;

  const results = useQuery({
    queryKey: queryKeys.portal.results(subjectKey),
    queryFn: () => portalApi.results(subject),
  });

  if (results.isPending) {
    return (
      <Card>
        <LoadingState label="Loading results" />
      </Card>
    );
  }
  if (results.isError) {
    return (
      <Card>
        <ErrorState error={results.error} onRetry={() => void results.refetch()} />
      </Card>
    );
  }
  if (results.data.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={<ClipboardCheck className="h-6 w-6" aria-hidden="true" />}
          title="No results yet"
          description="Results appear here once the school publishes them."
        />
      </Card>
    );
  }

  return (
    <Card>
      <ul className="divide-y divide-line">
        {results.data.map((result) => (
          <li key={result.id}>
            <Link
              to={withChild(ROUTES.myResult(result.examinationId))}
              className="flex flex-wrap items-center gap-3 px-4 py-3 transition-colors hover:bg-surface-2"
            >
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-ink">{result.examinationName}</span>
                <span className="block text-[11px] text-subtle">
                  {[result.termName, result.academicYearName, result.sectionName]
                    .filter(Boolean)
                    .join(' · ')}
                  {result.publishedAt !== undefined && ` · published ${formatDate(result.publishedAt)}`}
                </span>
              </span>
              <span className="text-sm font-bold tabular-nums text-ink">
                {percentText(result.percentage)}
              </span>
              {result.grade !== undefined && (
                <span className="text-sm font-semibold text-ink">{result.grade}</span>
              )}
              <Badge tone={result.passing ? 'success' : 'danger'}>
                {result.passing ? 'Pass' : 'Fail'}
              </Badge>
              <ChevronRight className="h-4 w-4 text-subtle" aria-hidden="true" />
            </Link>
          </li>
        ))}
      </ul>
    </Card>
  );
}
